import axios from 'axios';

export const getEnvVar = (name) => {
  return import.meta.env[name];
};

export function isHostedDevEnvironment() {
  return typeof window !== 'undefined' && /(^|\.)dev\.macumbatravel\.com$/.test(window.location.hostname);
}

export function getEnvironmentTimeout() {
  // On hosted dev, allow more time for AI processing
  return isHostedDevEnvironment() ? 120000 : 60000;
}

const getApiBaseUrl = () => {
  let baseUrl = getEnvVar('VITE_API_URL');

  // If the app is hosted on dev.macumbatravel.com but baseUrl is localhost,
  // switch to Cloud Run dev URL or provided fallback to avoid failed requests.
  const isHostedDev = isHostedDevEnvironment();
  const isLocalhostUrl = typeof baseUrl === 'string' && baseUrl.startsWith('http://localhost');

  if (isHostedDev && isLocalhostUrl) {
    baseUrl = getEnvVar('VITE_FALLBACK_API_URL_DEV') || 'https://api-dev.macumbatravel.com/api/v1';
  }

  // Relative path leverages Vite proxy — avoids CORS on localhost
  const isDev = import.meta.env.DEV;
  if (isDev && isLocalhostUrl) {
    baseUrl = '/api/v1';
  }

  if (!baseUrl) {
    baseUrl = '/api/v1';
  }

  return baseUrl;
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send httpOnly auth cookie cross-origin
  timeout: getEnvironmentTimeout(), // 60s standard, 120s on hosted dev
});

apiClient.interceptors.request.use(
  (config) => {
    // Mark start time for timing diagnostics
    // eslint-disable-next-line no-param-reassign
    config.metadata = { startTime: Date.now() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Quiet expected 401s from session probes — don't flood console or trigger auth churn
    const requestUrl = error.config?.url || '';
    const isSessionProbe = requestUrl.includes('/users/me');

    if (!isSessionProbe) {
      console.error('API Error:', error);
    }
    
    if (error.response?.status === 401) {
      // Skip 401 handling for auth endpoints (login/register are expected to return 401/403)
      const isAuthEndpoint = requestUrl.includes('/auth/login') ||
                             requestUrl.includes('/auth/register') ||
                             requestUrl.includes('/auth/password-reset');

      if (!isAuthEndpoint && !isSessionProbe) {
        // Unauthorized on a real API call — cookie is invalid/expired. Signal the app.
        // /users/me 401 is a startup probe, not an active session failure — skip it.
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        window.dispatchEvent(new CustomEvent('auth:redirect-home'));
      }
    }
    
    return Promise.reject(error);
  }
);

export function getApiUrl() {
  return getApiBaseUrl();
}

export default apiClient;
export { apiClient };